import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';
// Ensure <media-gallery> upgrades inside the quick-view modal on non-product pages.
import '@theme/media-gallery';

/** sessionStorage key for the Ajax cart snapshot taken before Buy Now checkout */
const BUY_NOW_PRESERVE_KEY = 'rsw-buy-now-preserved-cart';
const BUY_NOW_RESTORE_LOCK = 'rsw-buy-now-restore-lock';

/** @type {Map<string, Element>} */
const productGridCache = new Map();
/** @type {Map<string, Promise<Element | null>>} */
const productGridInflight = new Map();

/** Shared across all quick-add instances so only the latest click wins. */
let globalOpenGeneration = 0;

/**
 * Normalize product URLs so prefetch (?variant=) and click share one cache entry.
 * @param {string} url
 * @returns {string}
 */
function productGridCacheKey(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    return `${parsed.origin}${path}`.toLowerCase();
  } catch {
    return String(url || '').split('?')[0].split('#')[0].toLowerCase();
  }
}

/**
 * @param {Element} grid
 * @returns {string}
 */
function productIdFromGrid(grid) {
  const fromForm = grid.querySelector('product-form-component[data-product-id]');
  if (fromForm instanceof HTMLElement && fromForm.dataset.productId) return fromForm.dataset.productId;

  const fromAttr = grid.querySelector('[data-product-id]');
  if (fromAttr instanceof HTMLElement && fromAttr.dataset.productId) return fromAttr.dataset.productId;

  return grid instanceof HTMLElement ? grid.dataset.quickAddProductId || '' : '';
}

const QV_DELIVERY_ICON = `<svg width="28" height="27" viewBox="0 0 28 27" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M27.6268 23.1418C27.5542 23.2933 27.5009 23.4554 27.407 23.5941C27.119 24.0229 26.686 24.1168 26.2017 24.104C25.6961 24.089 25.694 24.0997 25.5233 24.5626C25.0796 25.7573 23.8806 26.5744 22.639 26.5274C21.3014 26.4762 20.1665 25.608 19.8081 24.3408C19.759 24.1658 19.6993 24.0954 19.5052 24.0954C16.158 24.1018 12.8108 24.0997 9.46357 24.0997C9.39317 24.0997 9.32277 24.1061 9.2353 24.1104C9.04757 24.8954 8.64437 25.5333 7.97877 25.992C7.46037 26.3504 6.8801 26.5317 6.25503 26.521C5.0305 26.504 3.72703 25.7594 3.30463 24.0997C2.91637 24.0997 2.5217 24.0976 2.12917 24.0997C1.38463 24.1061 0.887567 23.5941 0.889701 22.8453C0.893967 18.841 0.893967 14.8368 0.889701 10.8346C0.889701 10.7066 0.840634 10.5701 0.783034 10.4549C-1.40577 6.13063 1.20543 0.933832 5.9905 0.103965C9.90944 -0.576568 13.7153 2.1605 14.2934 6.0709C14.3169 6.2309 14.3404 6.38876 14.3702 6.58076C14.4662 6.58716 14.5708 6.59783 14.6732 6.59783C15.887 6.59997 17.103 6.6213 18.3169 6.59143C19.1553 6.5701 19.6694 7.1269 19.6545 7.91623C19.6204 9.54397 19.6438 11.1738 19.6438 12.8016C19.6438 12.9061 19.6438 13.0128 19.6438 13.1493C19.7697 13.1493 19.8657 13.1493 19.9617 13.1493C21.2481 13.1429 22.5345 13.1578 23.8209 13.1258C24.5782 13.1066 25.0966 13.3989 25.3953 14.0965C25.8433 15.144 26.2998 16.1872 26.7542 17.2325C26.8588 17.4714 26.9782 17.704 27.0742 17.945C27.2641 18.4165 27.4412 18.8922 27.6246 19.3658C27.6268 20.6224 27.6268 21.8832 27.6268 23.1418ZM2.0353 18.1093C7.54357 18.1093 13.0177 18.1093 18.494 18.1093C18.494 14.6426 18.494 11.1952 18.494 7.73276C17.1564 7.73276 15.8444 7.7349 14.5302 7.73063C14.3873 7.73063 14.3532 7.7925 14.3404 7.92477C14.3126 8.2021 14.2828 8.48157 14.2166 8.75037C13.5446 11.4426 11.9084 13.288 9.2481 14.0837C6.6177 14.8709 4.2625 14.2352 2.22943 12.3941C2.17397 12.345 2.11637 12.2981 2.03743 12.232C2.0353 14.2117 2.0353 16.1509 2.0353 18.1093ZM18.5025 19.2698C12.9964 19.2698 7.52437 19.2698 2.04597 19.2698C2.04597 19.8032 2.04597 20.3194 2.04597 20.8656C2.4833 20.8464 2.97183 20.8293 2.9825 21.4053C2.99317 22.0112 2.47477 21.9898 2.0225 22.0026C2.0225 22.2693 2.0289 22.5104 2.02037 22.7514C2.01397 22.9136 2.07797 22.9797 2.23797 22.9754C2.49823 22.969 2.76063 22.9562 3.0209 22.9797C3.24703 22.9989 3.31957 22.9136 3.37717 22.7024C3.99797 20.456 6.77983 19.7264 8.4097 21.3733C8.8513 21.8192 9.1137 22.3589 9.23957 22.9605C12.3393 22.9605 15.4198 22.9605 18.5025 22.9605C18.5025 21.7232 18.5025 20.5029 18.5025 19.2698ZM7.19157 13.2709C10.5558 13.2709 13.2673 10.5722 13.2737 7.2165C13.2801 3.8501 10.5601 1.1109 7.2065 1.10877C3.8081 1.1045 1.10943 3.83303 1.11157 7.2677C1.1137 10.568 3.8529 13.2709 7.19157 13.2709ZM19.6588 18.1093C21.743 18.1093 23.8145 18.1093 25.935 18.1093C25.4017 16.8976 24.8769 15.72 24.3649 14.536C24.2689 14.3141 24.1196 14.2544 23.902 14.2565C22.5793 14.265 21.2588 14.2693 19.9361 14.2757C19.8486 14.2757 19.7612 14.2842 19.6609 14.2885C19.6588 15.5706 19.6588 16.8336 19.6588 18.1093ZM19.6673 22.9648C19.6886 22.969 19.71 22.9733 19.7313 22.9797C19.7548 22.8901 19.7804 22.8026 19.8038 22.713C20.2006 21.192 21.7601 20.2128 23.2918 20.5456C24.5206 20.8122 25.2929 21.5717 25.6321 22.7792C25.6534 22.8517 25.7153 22.952 25.775 22.9669C26.3404 23.1034 26.4876 22.9818 26.4876 22.4122C26.4876 22.0517 26.4876 21.6933 26.4833 21.3328C26.4833 21.2368 26.4684 21.1429 26.462 21.0533C25.822 20.9701 25.6321 20.8314 25.6257 20.4624C25.6214 20.0976 25.7644 19.993 26.4406 19.865C26.4406 19.6666 26.4406 19.4682 26.4406 19.2762C24.1601 19.2762 21.9094 19.2762 19.6673 19.2762C19.6673 20.52 19.6673 21.7424 19.6673 22.9648ZM6.29557 21.6122C5.23743 21.5994 4.3841 22.4336 4.37557 23.4896C4.36703 24.52 5.2097 25.3797 6.24863 25.3989C7.28543 25.4181 8.14943 24.5648 8.16437 23.5088C8.1793 22.472 7.34517 21.625 6.29557 21.6122ZM20.8193 23.4917C20.8172 24.5498 21.6705 25.4096 22.7137 25.401C23.7526 25.3904 24.6017 24.5434 24.606 23.5109C24.6102 22.4592 23.7633 21.6144 22.7052 21.6144C21.6556 21.6122 20.8193 22.4464 20.8193 23.4917Z" fill="black"/><path d="M7.20006 12.1872C4.44379 12.1893 2.2038 9.95785 2.20166 7.20798C2.19953 4.43465 4.43526 2.19465 7.20006 2.19678C9.95206 2.19891 12.1814 4.43465 12.1835 7.19305C12.1857 9.95358 9.95846 12.185 7.20006 12.1872ZM7.20219 3.31038C5.07526 3.30185 3.31953 5.03198 3.31313 7.14611C3.3046 9.32211 5.03473 11.0864 7.18513 11.0949C9.32699 11.1034 11.0977 9.34558 11.0721 7.16531C11.0443 4.90185 9.22673 3.30185 7.20219 3.31038Z" fill="black"/><path d="M7.11905 23.5408C7.11692 24.0165 6.73718 24.3899 6.25932 24.3813C5.79852 24.3749 5.43372 23.9995 5.43372 23.5323C5.43585 23.0544 5.81558 22.6811 6.29345 22.6896C6.76065 22.7003 7.12118 23.0736 7.11905 23.5408Z" fill="black"/><path d="M22.7051 24.3812C22.2358 24.3812 21.8497 23.9908 21.8539 23.5236C21.8603 23.0671 22.2443 22.6917 22.703 22.6917C23.1723 22.6917 23.5648 23.0863 23.5563 23.5492C23.5456 24.0079 23.1638 24.3812 22.7051 24.3812Z" fill="black"/><path d="M7.74627 6.65969C8.07054 6.65969 8.36494 6.65542 8.65934 6.66182C9.054 6.66822 9.2908 6.87729 9.28867 7.20795C9.28654 7.52582 9.054 7.73488 8.67 7.74342C8.2028 7.75195 7.7356 7.75408 7.2684 7.74342C6.84174 7.73275 6.64547 7.52582 6.64334 7.10128C6.63907 6.42715 6.63907 5.75302 6.64334 5.07889C6.64547 4.66929 6.86094 4.41542 7.1916 4.41542C7.52227 4.41329 7.74414 4.67142 7.74627 5.07462C7.75054 5.58875 7.74627 6.10929 7.74627 6.65969Z" fill="black"/></svg>`;

/**
 * Tax + shipping lines under price (parity with PDP injectTaxNote / injectDelivery).
 * @param {Element} root
 */
function injectQuickViewTaxAndShipping(root) {
  const priceBlock =
    root.querySelector('.product-header product-price') ||
    root.querySelector('.product-details product-price') ||
    root.querySelector('product-price');
  if (!priceBlock) return;

  let tax = root.querySelector('.rsw-pdp-tax');
  if (!tax) {
    tax = document.createElement('p');
    tax.className = 'rsw-pdp-tax';
    tax.textContent =
      globalThis.Theme?.translations?.price_inclusive_of_taxes || 'Price inclusive of all taxes';
    priceBlock.insertAdjacentElement('afterend', tax);
  }

  if (root.querySelector('.rsw-pdp-delivery')) return;

  const timeline =
    globalThis.Theme?.translations?.delivery_timeline || 'Dubai: 1-2 Days, UAE: 3-5 days';
  const free = globalThis.Theme?.translations?.free_delivery_uae || 'Free Delivery UAE';
  const delivery = document.createElement('p');
  delivery.className = 'rsw-pdp-delivery';
  delivery.innerHTML = `${QV_DELIVERY_ICON}<span>${timeline}  <strong>${free}</strong></span>`;
  tax.insertAdjacentElement('afterend', delivery);
}

/** Selectors stripped before caching — keep PDP content (accordions, engraving). */
const QUICK_ADD_STRIP_SELECTORS = [
  'sticky-add-to-cart',
  '.rsw-pdp-gallery-toggle',
  '.rsw-pdp-gallery-wishlist',
  '.product-media-container__zoom-button',
  'shopify-accelerated-checkout',
  '.shopify-payment-button',
].join(', ');

const QV_WISHLIST_OUTLINE = `<svg class="wishlist-button__icon wishlist-button__icon--outline" xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none" aria-hidden="true" focusable="false"><path d="M10.7752 20.4463C10.6581 20.4463 10.5408 20.422 10.4314 20.3723C10.07 20.2076 1.56395 16.263 0.20005 9.23789C-0.32716 6.52005 0.202276 3.86851 1.61591 2.14651C2.75985 0.751666 4.40443 0.0100048 6.3729 5.74229e-05C8.64753 5.74229e-05 10.017 1.27888 10.7741 2.36756C11.5345 1.27446 12.9149 -0.00988995 15.1752 5.74229e-05C17.1448 0.0100048 18.7905 0.751666 19.9357 2.14651C21.347 3.8674 21.8753 6.51891 21.347 9.23897C19.9853 16.264 11.4781 20.2098 11.1167 20.3734C11.0095 20.422 10.8924 20.4463 10.7752 20.4463ZM6.40056 1.65688C4.90296 1.66351 3.73141 2.18186 2.89804 3.1976C1.79388 4.5427 1.39487 6.68251 1.82813 8.92177C2.88366 14.3641 9.2201 17.901 10.7752 18.6946C12.3303 17.901 18.6668 14.3641 19.7212 8.92177C20.1567 6.6814 19.7576 4.5416 18.6558 3.1976C17.8224 2.18297 16.6508 1.66569 15.1686 1.65795C12.5282 1.65795 11.5987 4.28519 11.5611 4.39682C11.4462 4.73172 11.13 4.95937 10.7763 4.95937C10.417 4.95827 10.101 4.7317 9.98822 4.3946C9.95174 4.28407 9.02114 1.65688 6.40056 1.65688Z" fill="currentColor"/></svg>`;

const QV_WISHLIST_FILLED = `<svg class="wishlist-button__icon wishlist-button__icon--filled" xmlns="http://www.w3.org/2000/svg" width="22" height="21" viewBox="0 0 22 21" fill="none" aria-hidden="true" focusable="false"><path d="M10.7752 20.4463C10.6581 20.4463 10.5408 20.422 10.4314 20.3723C10.07 20.2076 1.56395 16.263 0.20005 9.23789C-0.32716 6.52005 0.202276 3.86851 1.61591 2.14651C2.75985 0.751666 4.40443 0.0100048 6.3729 5.74229e-05C8.64753 5.74229e-05 10.017 1.27888 10.7741 2.36756C11.5345 1.27446 12.9149 -0.00988995 15.1752 5.74229e-05C17.1448 0.0100048 18.7905 0.751666 19.9357 2.14651C21.347 3.8674 21.8753 6.51891 21.347 9.23897C19.9853 16.264 11.4781 20.2098 11.1167 20.3734C11.0095 20.422 10.8924 20.4463 10.7752 20.4463Z" fill="currentColor"/></svg>`;

/**
 * @param {string} url
 * @returns {string}
 */
function productHandleFromUrl(url) {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\/products\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

/**
 * Ensure quick-view title row exists for wishlist placement.
 * @param {Element} root
 * @returns {HTMLElement | null}
 */
function ensureQuickViewTitleRow(root) {
  let titleRow = root.querySelector('.rsw-qv-title-row');
  if (titleRow instanceof HTMLElement) return titleRow;

  const titleHost =
    root.querySelector('.view-product-title') || root.querySelector('.product-header');
  if (!(titleHost instanceof HTMLElement)) return null;

  if (titleHost.classList.contains('view-product-title')) {
    titleHost.classList.add('rsw-qv-title-row');
    return titleHost;
  }

  const titleLink = titleHost.querySelector(':scope > a');
  if (!(titleLink instanceof HTMLElement)) return null;
  titleRow = document.createElement('div');
  titleRow.className = 'rsw-qv-title-row';
  titleLink.before(titleRow);
  titleRow.append(titleLink);
  return titleRow;
}

/**
 * Place a Kaktus-synced wishlist heart next to the quick-view title.
 * @param {Element} root
 * @param {{ productId?: string, productTitle?: string, productUrl?: string }} [context]
 */
function injectQuickViewWishlist(root, context = {}) {
  const titleRow = ensureQuickViewTitleRow(root);
  if (!titleRow) return;

  let wishlist = titleRow.querySelector('.wishlist-button--pdp');
  if (!(wishlist instanceof HTMLElement)) {
    wishlist = document.createElement('wishlist-button');
    wishlist.className = 'wishlist-button wishlist-button--pdp';
    wishlist.innerHTML = `<button type="button" class="wishlist-button__control button-unstyled">${QV_WISHLIST_OUTLINE}${QV_WISHLIST_FILLED}</button>`;
    const titleLink = titleRow.querySelector(':scope > a');
    if (titleLink) titleLink.after(wishlist);
    else titleRow.append(wishlist);
  }

  const productId =
    context.productId ||
    productIdFromGrid(root) ||
    root.querySelector('[data-product-id]')?.getAttribute?.('data-product-id') ||
    '';
  const productUrl =
    context.productUrl ||
    titleRow.querySelector('a')?.getAttribute('href') ||
    '';
  const productHandle = productHandleFromUrl(productUrl);
  const productTitle = context.productTitle || titleRow.querySelector('a')?.textContent?.trim() || '';

  wishlist.dataset.productId = productId;
  wishlist.dataset.productHandle = productHandle;
  wishlist.dataset.productTitle = productTitle;

  const control = wishlist.querySelector('.wishlist-button__control');
  if (!(control instanceof HTMLButtonElement)) return;

  const addLabelTemplate =
    globalThis.Theme?.translations?.add_to_wishlist || 'Add {{ product_name }} to wishlist';
  const removeLabelTemplate =
    globalThis.Theme?.translations?.remove_from_wishlist ||
    'Remove {{ product_name }} from wishlist';
  const labelFor = (template) =>
    String(template)
      .replace(/\{\{\s*product_name\s*\}\}/g, productTitle || 'product')
      .replace(/\[product_name\]/g, productTitle || 'product');

  const syncActive = () => {
    const active = Boolean(
      productId && window.ParajohnKaktusWishlist?.has?.(productId)
    );
    wishlist.toggleAttribute('data-active', active);
    control.setAttribute('aria-pressed', String(active));
    control.setAttribute('aria-label', active ? labelFor(removeLabelTemplate) : labelFor(addLabelTemplate));
  };

  syncActive();

  if (wishlist.dataset.rswBound === 'true') return;
  wishlist.dataset.rswBound = 'true';

  const onStoreChange = () => syncActive();
  document.addEventListener(
    window.ParajohnKaktusWishlist?.changeEvent || 'parajohn:kaktus-wishlist-update',
    onStoreChange
  );

  control.addEventListener('click', async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (control.disabled || (!productId && !productHandle)) return;

    control.disabled = true;
    try {
      if (typeof window.ParajohnKaktusWishlist?.toggle !== 'function') {
        console.warn('[quick-add] Kaktus wishlist bridge unavailable');
        return;
      }
      const active = await window.ParajohnKaktusWishlist.toggle({
        productId,
        handle: productHandle,
        title: productTitle,
      });
      wishlist.toggleAttribute('data-active', Boolean(active));
      control.setAttribute('aria-pressed', String(Boolean(active)));
      control.setAttribute(
        'aria-label',
        active ? labelFor(removeLabelTemplate) : labelFor(addLabelTemplate)
      );
    } catch (error) {
      console.warn('[quick-add] Wishlist toggle failed:', error);
    } finally {
      control.disabled = false;
    }
  });
}

/**
 * @param {Element} gridElement
 */
function stripQuickAddChrome(gridElement) {
  gridElement.querySelectorAll(QUICK_ADD_STRIP_SELECTORS).forEach((node) => node.remove());
}

/**
 * @returns {string}
 */
function cartApiRoot() {
  return window.Shopify?.routes?.root || '/';
}

/**
 * Layout qty + ATC on one row (parity with PDP layoutBuyRow).
 * @param {Element} root
 */
function layoutQuickViewBuyRow(root) {
  const buttons = root.querySelector('.product-form-buttons');
  if (!buttons) return;

  const qty = buttons.querySelector('.quantity-selector-wrapper');
  const atcHost =
    buttons.querySelector(':scope > *:has(.add-to-cart-button)') ||
    buttons.querySelector('.rsw-pdp-buy-row > *:has(.add-to-cart-button)') ||
    buttons.querySelector('add-to-cart-component')?.parentElement;
  if (!qty || !atcHost) return;

  let row = buttons.querySelector(':scope > .rsw-pdp-buy-row');
  if (!row) {
    row = document.createElement('div');
    row.className = 'rsw-pdp-buy-row';
    qty.before(row);
  }

  if (qty.parentElement !== row) row.append(qty);
  if (atcHost !== row && atcHost.parentElement !== row) row.append(atcHost);
}

/**
 * Base64url-encode a string for cart permalink `properties` (Shopify docs).
 * @param {string} value
 * @returns {string}
 */
function base64UrlEncode(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/**
 * @param {HTMLFormElement} form
 * @returns {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number } | null}
 */
function buildQuickViewBuyNowItem(form) {
  const formData = new FormData(form);
  const id = formData.get('id');
  if (!id) return null;

  const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = /^properties\[([^\]]+)\]$/.exec(String(key));
    if (match && String(value).length) properties[match[1]] = String(value);
  }

  /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }} */
  const item = { id: Number(id), quantity };
  if (Object.keys(properties).length) item.properties = properties;

  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) item.selling_plan = Number(sellingPlan);

  return item;
}

/**
 * Snapshot the current Ajax cart so Buy Now can restore it after checkout.
 * @returns {Promise<void>}
 */
async function preserveCartForBuyNow() {
  const rootUrl = cartApiRoot();
  const res = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Unable to read cart');

  const cart = await res.json();
  /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }[]} */
  const items = (cart.items || []).map((/** @type {Record<string, any>} */ line) => {
    /** @type {{ id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }} */
    const saved = {
      id: Number(line.variant_id || line.id),
      quantity: Number(line.quantity) || 1,
    };
    if (line.properties && Object.keys(line.properties).length) {
      /** @type {Record<string, string>} */
      const props = {};
      for (const [key, value] of Object.entries(line.properties)) {
        if (value != null && String(value).length) props[key] = String(value);
      }
      if (Object.keys(props).length) saved.properties = props;
    }
    const sellingPlanId = line.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlanId) saved.selling_plan = Number(sellingPlanId);
    return saved;
  });

  sessionStorage.setItem(BUY_NOW_PRESERVE_KEY, JSON.stringify({ items }));
}

/**
 * @param {{ id: number, quantity: number, properties?: Record<string, string> }} item
 * @returns {string}
 */
function buildBuyNowPermalink(item) {
  const rootUrl = cartApiRoot();
  let url = `${rootUrl}cart/${item.id}:${item.quantity}`;
  if (item.properties && Object.keys(item.properties).length) {
    const encoded = base64UrlEncode(JSON.stringify(item.properties));
    url += `?properties=${encodeURIComponent(encoded)}`;
  }
  return url;
}

/**
 * @param {Record<string, any>} cart
 */
function notifyCartRestored(cart) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      source: 'rsw-buy-now-restore',
      itemCount: cart.item_count,
      didError: false,
    },
  });
}

/**
 * After Buy Now, restore the shopper's previous Ajax cart (permalinks replace it).
 * @returns {Promise<void>}
 */
async function restorePreservedCartAfterBuyNow() {
  if (sessionStorage.getItem(BUY_NOW_RESTORE_LOCK)) return;
  const raw = sessionStorage.getItem(BUY_NOW_PRESERVE_KEY);
  if (!raw) return;

  sessionStorage.setItem(BUY_NOW_RESTORE_LOCK, '1');
  try {
    /** @type {{ items?: { id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number }[] }} */
    let saved;
    try {
      saved = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem(BUY_NOW_PRESERVE_KEY);
      return;
    }

    const rootUrl = cartApiRoot();
    const clearRes = await fetch(`${rootUrl}cart/clear.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!clearRes.ok) throw new Error('Unable to clear Buy Now remnant cart');

    const items = Array.isArray(saved.items) ? saved.items.filter((line) => line?.id && line.quantity > 0) : [];
    if (items.length) {
      const addRes = await fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });
      if (!addRes.ok) {
        const errorBody = await addRes.json().catch(() => null);
        throw new Error(errorBody?.description || errorBody?.message || 'Unable to restore cart');
      }
    }

    sessionStorage.removeItem(BUY_NOW_PRESERVE_KEY);

    const cartRes = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
    if (cartRes.ok) {
      notifyCartRestored(await cartRes.json());
    }
  } finally {
    sessionStorage.removeItem(BUY_NOW_RESTORE_LOCK);
  }
}

/**
 * Bind one-time restore when returning from Buy Now checkout (including bfcache back).
 */
function bindBuyNowCartRestore() {
  if (window.__rswBuyNowRestoreBound) return;
  window.__rswBuyNowRestoreBound = true;

  const run = () => {
    restorePreservedCartAfterBuyNow().catch((error) => {
      console.error('[quick-add] Failed to restore cart after Buy Now:', error);
    });
  };

  run();
  window.addEventListener('pageshow', run);
}

/**
 * Direct single-product checkout for the selected quick-view variant (cart permalink).
 * @param {Element} root
 * @returns {Promise<boolean>}
 */
async function checkoutQuickViewProductOnly(root) {
  const form = root.querySelector('form[data-type="add-to-cart-form"]');
  if (!(form instanceof HTMLFormElement)) return false;
  if (!form.checkValidity()) {
    form.reportValidity();
    return false;
  }

  const item = buildQuickViewBuyNowItem(form);
  if (!item) return false;

  try {
    await preserveCartForBuyNow();
  } catch (error) {
    console.warn('[quick-add] Could not snapshot cart before Buy Now:', error);
  }

  const rootUrl = cartApiRoot();

  if (item.selling_plan) {
    const clearRes = await fetch(`${rootUrl}cart/clear.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!clearRes.ok) throw new Error('Unable to prepare checkout');

    const addRes = await fetch(`${rootUrl}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items: [item] }),
    });
    if (!addRes.ok) {
      const errorBody = await addRes.json().catch(() => null);
      throw new Error(errorBody?.description || errorBody?.message || 'Unable to start checkout');
    }

    window.location.href = `${rootUrl}checkout`;
    return true;
  }

  window.location.href = buildBuyNowPermalink(item);
  return true;
}

/**
 * Inject Buy it Now under ATC (no floating bar — modal only).
 * @param {Element} root
 */
function injectQuickViewBuyNow(root) {
  layoutQuickViewBuyRow(root);

  const buttons = root.querySelector('.product-form-buttons');
  const addButton = root.querySelector('.add-to-cart-button:not(.sticky-add-to-cart__button)');
  if (!buttons || !(addButton instanceof HTMLButtonElement)) return;

  let buyNow = buttons.querySelector('.rsw-pdp-buy-now');
  if (!(buyNow instanceof HTMLButtonElement)) {
    buyNow = document.createElement('button');
    buyNow.type = 'button';
    buyNow.className = 'rsw-pdp-buy-now';
    buttons.append(buyNow);
  }

  buyNow.textContent =
    globalThis.Theme?.translations?.buy_it_now || 'Buy it Now';

  let buyNowRow = buttons.querySelector(':scope > .rsw-pdp-buy-now-row');
  if (!buyNowRow) {
    buyNowRow = document.createElement('div');
    buyNowRow.className = 'rsw-pdp-buy-now-row';
    buyNow.before(buyNowRow);
  }

  buyNowRow.querySelectorAll('.rsw-pdp-buy-now-cart').forEach((el) => el.remove());
  if (buyNow.parentElement !== buyNowRow) buyNowRow.append(buyNow);

  const syncPurchaseDisabled = () => {
    if (buyNow.dataset.rswBusy === 'true') return;
    const unavailable = Boolean(addButton.disabled);
    buyNow.disabled = unavailable;
    buyNow.setAttribute('aria-disabled', String(unavailable));
  };

  if (buyNow._rswDisabledObserver instanceof MutationObserver) {
    buyNow._rswDisabledObserver.disconnect();
  }
  const purchaseObserver = new MutationObserver(syncPurchaseDisabled);
  purchaseObserver.observe(addButton, { attributes: true, attributeFilter: ['disabled'] });
  buyNow._rswDisabledObserver = purchaseObserver;
  syncPurchaseDisabled();

  if (buyNow.dataset.rswBound === 'true') return;
  buyNow.dataset.rswBound = 'true';

  buyNow.addEventListener('click', async () => {
    if (addButton.disabled || buyNow.disabled || buyNow.dataset.rswBusy === 'true') return;

    buyNow.dataset.rswBusy = 'true';
    buyNow.disabled = true;
    try {
      const started = await checkoutQuickViewProductOnly(root);
      if (!started) {
        buyNow.disabled = Boolean(addButton.disabled);
        buyNow.dataset.rswBusy = 'false';
      }
    } catch (error) {
      console.error('[quick-add] Buy now failed:', error);
      buyNow.disabled = Boolean(addButton.disabled);
      buyNow.dataset.rswBusy = 'false';
    }
  });
}

/**
 * Always return a fresh clone so morph/variant updates never mutate the cache.
 * @param {Element | null} grid
 * @returns {Element | null}
 */
function cloneProductGrid(grid) {
  return grid ? /** @type {Element} */ (grid.cloneNode(true)) : null;
}

export class QuickAddComponent extends Component {
  /** @type {number} */
  #openGeneration = 0;
  /** @type {boolean} */
  #opening = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.addEventListener('pointerenter', this.#prefetchProductPage, { passive: true });
    this.addEventListener('focusin', this.#prefetchProductPage, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerenter', this.#prefetchProductPage);
    this.removeEventListener('focusin', this.#prefetchProductPage);
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Warm the product-page cache before click so the modal can open immediately.
   * Public so product-card hover can prefetch without aborting in-flight loads.
   * @param {string} [url]
   */
  prefetchProductPage = (url) => {
    if (this.dataset.usesSellingPlans === 'true') return;
    const productPageUrl = url || this.productPageUrl;
    if (!productPageUrl) return;
    const cacheKey = productGridCacheKey(productPageUrl);
    if (productGridCache.has(cacheKey) || productGridInflight.has(cacheKey)) return;
    void this.#loadProductGrid(productPageUrl, { productId: this.dataset.productId || '' });
  };

  /**
   * Warm the product-page cache before click so the modal can open immediately.
   */
  #prefetchProductPage = () => {
    this.prefetchProductPage();
  };

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * @param {boolean} isLoading
   */
  #setModalLoading(isLoading) {
    const dialog = document.querySelector('#quick-add-dialog dialog');
    if (!(dialog instanceof HTMLElement)) return;

    dialog.classList.toggle('is-loading', isLoading);
    dialog.toggleAttribute('aria-busy', isLoading);
  }

  /** Clear prior product HTML so a failed/stale fetch never leaves the wrong product visible. */
  #clearModalContent() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) modalContent.replaceChildren();
  }

  /**
   * Collapse empty variant shells left when products have no size/set options.
   * @param {Element} root
   */
  #collapseEmptyVariantUI(root) {
    root.querySelectorAll('.variant-picker, variant-picker').forEach((picker) => {
      if (!(picker instanceof HTMLElement)) return;
      const hasOptions =
        picker.querySelector(
          '.variant-option, .variant-option__button-label, .variant-option--set-product-links, a.variant-option__button-label'
        ) != null;
      if (!hasOptions) {
        picker.hidden = true;
        picker.style.display = 'none';
      }
    });

    root.querySelectorAll('.divider').forEach((divider) => {
      if (divider instanceof HTMLElement) divider.hidden = true;
    });
  }

  /**
   * Fetch + cache product grid HTML for a product URL.
   * Returns a fresh clone every time (cache stays intact).
   * @param {string} productPageUrl
   * @param {{ productId?: string }} [options]
   * @returns {Promise<Element | null>}
   */
  async #loadProductGrid(productPageUrl, options = {}) {
    const cacheKey = productGridCacheKey(productPageUrl);
    const expectedProductId = options.productId || '';

    const cached = productGridCache.get(cacheKey);
    if (cached) {
      const cachedId = productIdFromGrid(cached);
      if (expectedProductId && cachedId && cachedId !== expectedProductId) {
        productGridCache.delete(cacheKey);
      } else {
        return cloneProductGrid(cached);
      }
    }

    const pending = productGridInflight.get(cacheKey);
    if (pending) {
      const resolved = await pending;
      if (!resolved) return null;
      const resolvedId = productIdFromGrid(resolved);
      if (expectedProductId && resolvedId && resolvedId !== expectedProductId) {
        productGridCache.delete(cacheKey);
        return null;
      }
      return cloneProductGrid(resolved);
    }

    const request = this.#fetchProductDocument(productPageUrl)
      .then((html) => {
        if (!html) return null;
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (!gridElement) {
          console.warn('[quick-add] Missing [data-product-grid-content] for', productPageUrl);
          return null;
        }

        stripQuickAddChrome(gridElement);

        const productGrid = /** @type {HTMLElement} */ (gridElement.cloneNode(true));
        const fetchedId = productIdFromGrid(productGrid) || expectedProductId;
        if (fetchedId) productGrid.dataset.quickAddProductId = fetchedId;
        productGrid.dataset.quickAddCacheKey = cacheKey;

        if (expectedProductId && fetchedId && fetchedId !== expectedProductId) {
          console.warn('[quick-add] Fetched product id mismatch', expectedProductId, fetchedId);
          return null;
        }

        productGridCache.set(cacheKey, productGrid);
        return productGrid;
      })
      .catch((error) => {
        console.warn('[quick-add] Load grid failed:', error);
        return null;
      })
      .finally(() => {
        productGridInflight.delete(cacheKey);
      });

    productGridInflight.set(cacheKey, request);
    const resolved = await request;
    return cloneProductGrid(resolved);
  }

  /**
   * @param {number} generation
   * @returns {boolean}
   */
  #isCurrentOpen(generation) {
    return generation === globalOpenGeneration;
  }

  /**
   * @param {number} generation
   * @returns {boolean}
   */
  #canCommitOpen(generation) {
    if (!this.#isCurrentOpen(generation)) return false;
    const dialog = document.querySelector('#quick-add-dialog dialog');
    return dialog instanceof HTMLDialogElement && dialog.open;
  }

  /**
   * DialogComponent.showDialog() defers showModal() to requestAnimationFrame.
   * Cache hits can finish before that frame, so #canCommitOpen aborts and leaves
   * an empty overlay. Wait until the dialog is actually open.
   * @returns {Promise<boolean>}
   */
  #waitForDialogOpen() {
    const dialog = document.querySelector('#quick-add-dialog dialog');
    if (!(dialog instanceof HTMLDialogElement)) return Promise.resolve(false);
    if (dialog.open) return Promise.resolve(true);

    const dialogComponent = document.getElementById('quick-add-dialog');

    return new Promise((resolve) => {
      let settled = false;
      const finish = (ok) => {
        if (settled) return;
        settled = true;
        resolve(ok);
      };

      const onOpen = () => finish(dialog.open);
      dialogComponent?.addEventListener(DialogOpenEvent.eventName, onOpen, { once: true });

      // Match DialogComponent.showDialog scheduling (one rAF), plus one more for safety.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => finish(dialog.open));
      });

      setTimeout(() => finish(dialog.open), 400);
    });
  }

  /**
   * Bind engraving accordion inside the modal (PDP script is product-template only).
   * @param {Element} root
   */
  #bindCustomise(root) {
    const widget = root.querySelector('[data-rsw-customise]');
    if (!(widget instanceof HTMLElement) || widget.dataset.rswBound === 'true') return;

    widget.dataset.rswBound = 'true';
    const trigger = widget.querySelector('[data-rsw-customise-trigger]');
    const input = /** @type {HTMLInputElement | null} */ (widget.querySelector('[data-rsw-engraving-input]'));
    const panel = widget.querySelector('[data-rsw-customise-panel]');
    const form = widget.closest('form') || root.querySelector('form[data-type="add-to-cart-form"]');

    const setOpen = (open) => {
      widget.classList.toggle('is-open', open);
      trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel instanceof HTMLElement) {
        panel.inert = !open;
        panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
      if (input) {
        if (open) {
          input.disabled = false;
          input.required = true;
          input.setAttribute('aria-required', 'true');
          window.requestAnimationFrame(() => input.focus());
        } else {
          input.required = false;
          input.removeAttribute('aria-required');
          input.setCustomValidity('');
          input.value = '';
          input.disabled = true;
        }
      }
    };

    trigger?.addEventListener('click', () => {
      setOpen(!widget.classList.contains('is-open'));
    });

    input?.addEventListener('keydown', (event) => {
      const isSpace = event.key === ' ' || event.key === 'Spacebar' || event.code === 'Space';
      if (!isSpace) return;
      const inputEl = /** @type {HTMLInputElement} */ (event.target);
      if (inputEl.selectionStart === 0) {
        event.preventDefault();
      }
    });

    input?.addEventListener('beforeinput', (event) => {
      const inputEl = /** @type {HTMLInputElement} */ (event.target);
      if (inputEl.selectionStart !== 0) return;
      if (typeof event.data === 'string' && /^\s/.test(event.data)) {
        if (/^\s+$/.test(event.data)) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        const stripped = event.data.replace(/^\s+/, '');
        if (!stripped) return;
        const { selectionStart, selectionEnd, value } = inputEl;
        const start = selectionStart ?? 0;
        const end = selectionEnd ?? start;
        inputEl.value = `${value.slice(0, start)}${stripped}${value.slice(end)}`;
        const caret = start + stripped.length;
        inputEl.setSelectionRange(caret, caret);
        inputEl.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    input?.addEventListener('input', () => {
      const stripped = input.value.replace(/^\s+/, '');
      if (stripped !== input.value) {
        const offset = input.value.length - stripped.length;
        const start = Math.max(0, (input.selectionStart ?? 0) - offset);
        const end = Math.max(0, (input.selectionEnd ?? 0) - offset);
        input.value = stripped;
        input.setSelectionRange(start, end);
      }
      input.setCustomValidity(input.value.trim() ? '' : 'Please enter your engraving text.');
    });

    form?.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('.add-to-cart-button, .rsw-pdp-buy-now')) return;
        if (!widget.classList.contains('is-open') || !input || input.disabled) return;

        if (!input.value.trim()) {
          input.setCustomValidity('Please enter your engraving text.');
          input.reportValidity();
          event.preventDefault();
          event.stopPropagation();
        } else {
          input.setCustomValidity('');
        }
      },
      true
    );
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Capture at click time — variant/url on the card can change while we fetch.
    const currentUrl = this.productPageUrl;
    const productId = this.dataset.productId || '';
    const productTitle = this.dataset.productTitle || '';

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    if (!currentUrl) return;

    // Global generation so a slower response from product A cannot overwrite product B.
    const generation = ++globalOpenGeneration;
    this.#openGeneration = generation;
    this.#opening = true;

    this.#openQuickAddModal();
    this.#setModalLoading(true);
    // Drop previous product immediately so the wrong PDP never stays visible.
    this.#clearModalContent();

    // Wait for showModal() — otherwise a cache hit races the rAF and aborts with an empty overlay.
    const dialogReady = await this.#waitForDialogOpen();
    if (!this.#isCurrentOpen(generation) || !dialogReady) {
      if (this.#isCurrentOpen(generation)) this.#closeQuickAddModal();
      return;
    }

    const cacheKey = productGridCacheKey(currentUrl);

    try {
      let productGrid = await this.#loadProductGrid(currentUrl, { productId });
      if (!this.#canCommitOpen(generation)) {
        // Generation still current but dialog not open (close raced open) — don't leave lock/overlay.
        if (this.#isCurrentOpen(generation)) this.#closeQuickAddModal();
        return;
      }

      // One retry with a fresh fetch if cache/morph produced nothing useful
      if (!productGrid) {
        productGridCache.delete(cacheKey);
        productGrid = await this.#loadProductGrid(currentUrl, { productId });
        if (!this.#canCommitOpen(generation)) {
          if (this.#isCurrentOpen(generation)) this.#closeQuickAddModal();
          return;
        }
      }

      if (!productGrid) {
        console.warn('[quick-add] No product grid for', currentUrl);
        if (this.#isCurrentOpen(generation)) this.#closeQuickAddModal();
        return;
      }

      const gridProductId = productIdFromGrid(productGrid);
      if (productId && gridProductId && gridProductId !== productId) {
        console.warn('[quick-add] Refusing to show mismatched product', productId, gridProductId);
        productGridCache.delete(cacheKey);
        if (this.#isCurrentOpen(generation)) this.#closeQuickAddModal();
        return;
      }

      // Final gate immediately before DOM write — prevents stale morph after a newer click.
      if (!this.#canCommitOpen(generation)) {
        if (this.#isCurrentOpen(generation)) this.#closeQuickAddModal();
        return;
      }

      await this.updateQuickAddModal(productGrid, {
        productTitle,
        productUrl: currentUrl,
        generation,
      });
      if (!this.#canCommitOpen(generation)) {
        if (this.#isCurrentOpen(generation)) this.#closeQuickAddModal();
        return;
      }

      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent?.childElementCount) {
        this.#collapseEmptyVariantUI(modalContent);
        this.#bindCustomise(modalContent);
      } else {
        console.warn('[quick-add] Modal stayed empty after morph for', currentUrl);
        productGridCache.delete(cacheKey);
        if (this.#isCurrentOpen(generation)) this.#closeQuickAddModal();
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[quick-add] Failed to open product:', error);
      }
      productGridCache.delete(cacheKey);
      if (this.#isCurrentOpen(generation)) this.#closeQuickAddModal();
    } finally {
      if (this.#isCurrentOpen(generation)) {
        this.#setModalLoading(false);
      }
      if (this.#openGeneration === generation) {
        this.#opening = false;
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    globalOpenGeneration += 1;
    this.#openGeneration = globalOpenGeneration;
    this.#opening = false;
    this.#setModalLoading(false);

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page document (no shared abort — avoids hover aborting open loads).
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async #fetchProductDocument(productPageUrl) {
    if (!productPageUrl) return null;

    const controller = new AbortController();
    // Hung network left the quick-view modal in is-loading with scroll-lock forever.
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(productPageUrl, { signal: controller.signal });

      if (!response.ok) {
        console.warn(`[quick-add] Failed to fetch product page: HTTP ${response.status}`);
        return null;
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error?.name === 'AbortError') {
        console.warn('[quick-add] Fetch timed out or aborted for', productPageUrl);
        return null;
      }
      console.warn('[quick-add] Fetch error:', error);
      return null;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * @deprecated Prefer prefetchProductPage — kept for any external callers.
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    // Route through the cache-safe loader; discard the grid clone and return null document.
    // Callers that need HTML should use #loadProductGrid / prefetchProductPage.
    await this.#loadProductGrid(productPageUrl);
    return null;
  }

  /**
   * Place product-details accordions above the sticky qty/ATC row (inside the form).
   * Run only on the live modal tree after morph — never mutate the fetch clone first.
   * @param {Element} root
   */
  #placeAccordionsAboveBuyButtons(root) {
    try {
      const accordions = root.querySelector('.rsw-pdp-accordions');
      const formButtons = root.querySelector('.product-form-buttons');
      if (!accordions || !formButtons) return;

      const form = formButtons.closest('form');
      const parent = form || formButtons.parentElement;
      if (!parent || accordions.parentElement === parent && accordions.nextElementSibling === formButtons) {
        return;
      }

      parent.insertBefore(accordions, formButtons);
    } catch (error) {
      console.warn('[quick-add] Could not reposition accordions:', error);
    }
  }

  /**
   * Keep stock status above Color in quick view.
   * Place as a sibling before <variant-picker> — never inside the picker form
   * (variant morph would remove nested inventory).
   * @param {Element} root
   */
  #placeInventoryAboveColor(root) {
    try {
      const inventory = root.querySelector('product-inventory');
      if (!inventory) return;

      const inventoryHost = inventory.closest('shopify-block, .shopify-block') || inventory;
      const picker = root.querySelector('variant-picker');
      if (!picker) return;

      const pickerHost = picker.closest('shopify-block, .shopify-block') || picker;
      if (!pickerHost.parentElement) return;

      if (picker.contains(inventoryHost)) {
        pickerHost.parentElement.insertBefore(inventoryHost, pickerHost);
        return;
      }

      if (inventoryHost.nextElementSibling === pickerHost) return;
      pickerHost.parentElement.insertBefore(inventoryHost, pickerHost);
    } catch (error) {
      console.warn('[quick-add] Could not reposition inventory:', error);
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @param {{ productTitle?: string, productUrl?: string, generation?: number }} [context]
   */
  async updateQuickAddModal(productGrid, context = {}) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const generation = context.generation;
    if (typeof generation === 'number' && !this.#canCommitOpen(generation)) return;

    const productTitle = context.productTitle ?? this.dataset.productTitle ?? '';
    const productUrl = context.productUrl ?? this.productPageUrl;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const inventoryEl = productGrid.querySelector('product-inventory');
      const inventory = inventoryEl?.closest('shopify-block, .shopify-block') || inventoryEl;
      const tabby = productGrid.querySelector('.rsw-pdp-tabby');
      const setLinks = productGrid.querySelector('.variant-option--set-product-links')?.closest('.variant-picker, .spacing-style');
      const accordions = productGrid.querySelector('.rsw-pdp-accordions');
      const productPrice = productGrid.querySelector('product-price');
      const titleLink = document.createElement('a');
      titleLink.textContent = productTitle;
      titleLink.href = productUrl;

      const titleRow = document.createElement('div');
      titleRow.className = 'rsw-qv-title-row';
      titleRow.appendChild(titleLink);

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(titleRow);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (inventory) {
        productGrid.appendChild(inventory);
      }
      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      } else if (setLinks) {
        productGrid.appendChild(setLinks);
      }
      if (tabby) {
        productGrid.appendChild(tabby);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      // Pull accordions out before removing .product-details or they are discarded
      if (accordions) {
        productGrid.appendChild(accordions);
      }

      productDetails?.remove();
    }

    if (typeof generation === 'number' && !this.#canCommitOpen(generation)) return;

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    // Customise → product details accordions → sticky qty/ATC (live modal only)
    this.#placeAccordionsAboveBuyButtons(modalContent);
    this.#placeInventoryAboveColor(modalContent);

    injectQuickViewTaxAndShipping(modalContent);
    injectQuickViewWishlist(modalContent, {
      productId: productIdFromGrid(modalContent) || this.dataset.productId || '',
      productTitle,
      productUrl,
    });
    injectQuickViewBuyNow(modalContent);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    // Invalidate late responses so they cannot refill a closed dialog.
    globalOpenGeneration += 1;

    const dialog = this.querySelector('dialog');
    dialog?.classList.remove('is-loading');
    dialog?.removeAttribute('aria-busy');

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

bindBuyNowCartRestore();
